/**
 * `TransferCreditsModal` — move N session credits from THIS member (A) to
 * another member (B). Dispatches the `transfer_member_credits` canonical
 * action, which in one atomic tx decrements A's chosen package and mints a
 * `source='transfer'` grant on B (inheriting A's session type + expiry).
 *
 * A's transferable packages are threaded down from the already-loaded member
 * detail (no extra fetch); the destination member is found with the same
 * debounced `lookupOptions("members", q)` search the calendar uses.
 */

import { useCallback, useEffect, useState } from "react";
import { api, type MemberDetailPackage } from "../lib/api";
import { dispatchAction } from "../lib/dispatch";

interface MemberOption {
  readonly value: string;
  readonly label: string;
  readonly sublabel?: string;
}

export function TransferCreditsModal({
  fromMemberId,
  fromMemberName,
  sourcePackages,
  onDone,
}: {
  readonly fromMemberId: string;
  readonly fromMemberName?: string;
  /** A's packages (from the loaded member detail); filtered to transferable. */
  readonly sourcePackages: ReadonlyArray<MemberDetailPackage>;
  /** Called after a successful transfer (refresh the member page). */
  readonly onDone: () => void;
}): JSX.Element {
  const transferable = sourcePackages.filter(
    (p) => p.status === "active" && p.remaining_sessions > 0,
  );

  const [sourcePackageId, setSourcePackageId] = useState(
    transferable[0]?.id ?? "",
  );
  const selectedSource = transferable.find((p) => p.id === sourcePackageId);
  const maxSessions = Math.min(selectedSource?.remaining_sessions ?? 0, 200);

  const [sessions, setSessions] = useState("1");
  const [destQuery, setDestQuery] = useState("");
  const [destOptions, setDestOptions] = useState<
    ReadonlyArray<MemberOption> | null
  >(null);
  const [searching, setSearching] = useState(false);
  const [destId, setDestId] = useState("");
  const [destLabel, setDestLabel] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  // Debounced destination-member search (name / email / phone ILIKE), same as
  // the calendar's book-member picker. Hidden until the operator types.
  useEffect(() => {
    const q = destQuery.trim();
    if (q === "" || destId !== "") {
      setDestOptions(null);
      setSearching(false);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const handle = setTimeout(() => {
      void (async () => {
        try {
          const res = await api.lookupOptions("members", q);
          if (!cancelled) {
            // Never offer the source member as a destination.
            setDestOptions(res.options.filter((o) => o.value !== fromMemberId));
          }
        } catch (e) {
          if (!cancelled) setError(`Couldn't load members: ${(e as Error).message}`);
        } finally {
          if (!cancelled) setSearching(false);
        }
      })();
    }, 220);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [destQuery, destId, fromMemberId]);

  const submit = useCallback(async () => {
    const n = Number(sessions.trim());
    if (!Number.isInteger(n) || n < 1 || n > maxSessions) {
      setError(`Enter a whole number of sessions (1–${maxSessions}).`);
      return;
    }
    if (sourcePackageId === "") {
      setError("Pick a source package.");
      return;
    }
    if (destId === "") {
      setError("Pick a destination member.");
      return;
    }
    if (destId === fromMemberId) {
      setError("Can't transfer credits to the same member.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await dispatchAction("transfer_member_credits", {
        from_member_id: fromMemberId,
        to_member_id: destId,
        sessions: n,
        source_package_id: sourcePackageId,
        ...(note.trim() !== "" ? { note: note.trim() } : {}),
      });
      setBusy(false);
      if (!r.ok) {
        setError(`Transfer failed: ${r.message || r.code}`);
        return;
      }
      setDone(
        `Transferred ${n} session${n === 1 ? "" : "s"} to ${destLabel || "the member"}.`,
      );
      onDone();
    } catch (e) {
      setBusy(false);
      setError((e as Error).message);
    }
  }, [
    sessions,
    maxSessions,
    sourcePackageId,
    destId,
    destLabel,
    fromMemberId,
    note,
    onDone,
  ]);

  if (transferable.length === 0) {
    return (
      <p className="xfer-empty">
        {fromMemberName ?? "This member"} has no active credits to transfer.
      </p>
    );
  }

  if (done !== null) {
    return <p className="xfer-done">✅ {done}</p>;
  }

  return (
    <div className="xfer-modal">
      <label className="xfer-field">
        <span>Source package</span>
        <select
          value={sourcePackageId}
          onChange={(e) => {
            setSourcePackageId(e.target.value);
            setSessions("1");
          }}
        >
          {transferable.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label} · {p.remaining_sessions} left
            </option>
          ))}
        </select>
      </label>

      <label className="xfer-field">
        <span>Sessions to move (max {maxSessions})</span>
        <input
          type="number"
          min={1}
          max={maxSessions}
          value={sessions}
          onChange={(e) => setSessions(e.target.value)}
        />
      </label>

      <div className="xfer-field">
        <span>Destination member</span>
        {destId !== "" ? (
          <div className="xfer-dest-chip">
            <span>{destLabel}</span>
            <button
              type="button"
              className="xfer-dest-clear"
              onClick={() => {
                setDestId("");
                setDestLabel("");
                setDestQuery("");
              }}
            >
              Change
            </button>
          </div>
        ) : (
          <>
            <input
              type="text"
              placeholder="Search by name, email or phone…"
              value={destQuery}
              onChange={(e) => setDestQuery(e.target.value)}
            />
            {searching ? <p className="xfer-hint">Searching…</p> : null}
            {destOptions !== null && destOptions.length > 0 ? (
              <ul className="xfer-dest-list">
                {destOptions.map((o) => (
                  <li key={o.value}>
                    <button
                      type="button"
                      onClick={() => {
                        setDestId(o.value);
                        setDestLabel(o.label);
                        setDestOptions(null);
                      }}
                    >
                      {o.label}
                      {o.sublabel ? (
                        <span className="xfer-dest-sub"> · {o.sublabel}</span>
                      ) : null}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            {destOptions !== null &&
            destOptions.length === 0 &&
            !searching ? (
              <p className="xfer-hint">No members match.</p>
            ) : null}
          </>
        )}
      </div>

      <label className="xfer-field">
        <span>Note (optional)</span>
        <input
          type="text"
          maxLength={500}
          placeholder="e.g. moved from partner's account"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </label>

      <div className="xfer-actions">
        <button
          type="button"
          className="xfer-submit"
          disabled={busy || destId === "" || maxSessions < 1}
          onClick={() => void submit()}
        >
          {busy ? "Transferring…" : "Transfer credits"}
        </button>
      </div>
      {error !== null ? <p className="cal-action-error">{error}</p> : null}
    </div>
  );
}
